import { useState } from 'react';
import { Button, Modal, Select, Space, Spin, Table, Typography } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { enumsApi, type MetaEnumValueDto } from '@api/enumsApi';

interface EnumPickerProps {
  /** BAS-name перерахування (з MetaAttribute.referenceEntity після зрізання "BasEnum:"). */
  acqEnumName: string;
  /** Збережене значення (BAS-name значення, наприклад "АналогичноPOP3"). */
  acqValue?: string | null;
  acqOnChange?: (acqValue: string | null) => void;
  acqReadOnly?: boolean;
  acqAllowClear?: boolean;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
}

/**
 * Picker для BAS-перерахування. Завантажує values з /api/metadata/enums/{name}.
 * Зберігає у data BAS-name значення (як у Phase 2 import). Показує UA-synonym.
 *
 * UI: Select з опціями + кнопка "📁" (модалка-таблиця для довгих enum'ів).
 * Pattern такий же, як у ReferencePicker — для UX-консистентності.
 */
export default function EnumPicker({
  acqEnumName, acqValue, acqOnChange, acqReadOnly = false,
  acqAllowClear = true, acqDisabled, acqSize = 'middle',
}: EnumPickerProps) {
  const acqQ = useQuery({
    queryKey: ['enum', acqEnumName],
    queryFn: () => enumsApi.getEnum(acqEnumName),
    staleTime: 10 * 60_000,
  });

  const [acqListOpen, setAcqListOpen] = useState(false);

  const acqValues = acqQ.data?.values ?? [];

  const acqOptions = acqValues.map((v) => ({
    value: v.basName,
    label: v.uaName,
  }));

  // Read-only — показуємо UA-synonym обраного
  if (acqReadOnly) {
    if (!acqValue) return <Typography.Text type="secondary">—</Typography.Text>;
    if (acqQ.isLoading) return <Spin size="small" />;
    const acqMatched = acqValues.find((v) => v.basName === acqValue);
    return <Typography.Text>{acqMatched?.uaName ?? acqValue}</Typography.Text>;
  }

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          showSearch
          allowClear={acqAllowClear}
          filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          placeholder={acqQ.isLoading ? 'Завантаження…' : 'Оберіть…'}
          size={acqSize}
          disabled={acqDisabled || acqQ.isLoading}
          loading={acqQ.isLoading}
          value={acqValue ?? undefined}
          onChange={(v) => acqOnChange?.(v ?? null)}
          options={acqOptions}
          style={{ width: '100%' }}
        />
        <Button
          size={acqSize}
          icon={<FolderOpenOutlined />}
          disabled={acqDisabled || acqQ.isLoading}
          onClick={() => setAcqListOpen(true)}
          title="Відкрити список значень"
        />
      </Space.Compact>
      <EnumListModal
        acqOpen={acqListOpen}
        acqTitle={acqQ.data?.uaName ?? acqEnumName}
        acqValues={acqValues}
        acqOnCancel={() => setAcqListOpen(false)}
        acqOnSelect={(basName) => { setAcqListOpen(false); acqOnChange?.(basName); }}
      />
    </>
  );
}

function EnumListModal({
  acqOpen, acqTitle, acqValues, acqOnCancel, acqOnSelect,
}: {
  acqOpen: boolean;
  acqTitle: string;
  acqValues: MetaEnumValueDto[];
  acqOnCancel: () => void;
  acqOnSelect: (acqBasName: string) => void;
}) {
  return (
    <Modal
      open={acqOpen}
      title={`Оберіть: ${acqTitle}`}
      onCancel={acqOnCancel}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Table<MetaEnumValueDto>
        rowKey="basName"
        size="small"
        dataSource={acqValues}
        pagination={false}
        columns={[
          { title: 'Назва', dataIndex: 'uaName' },
          { title: 'BAS', dataIndex: 'basName', width: 200, render: (v) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{v}</Typography.Text> },
        ]}
        onRow={(row) => ({
          onClick: () => acqOnSelect(row.basName),
          style: { cursor: 'pointer' },
        })}
      />
    </Modal>
  );
}
